import { ReactNode, useEffect, useMemo, useRef } from "react";
import { DevCycleProvider, DevCycleUser } from "@devcycle/react-client-sdk";
import { setUser } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";

import { betterAuthSessionQueryOptions } from "lib/hooks/api/auth/betterAuthSessionQuery";
import { useAcknowledgeSignupConversion } from "lib/hooks/api/auth/useAcknowledgeSignupConversion";
import { CurrentUserData, useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useUnifiedContacts } from "lib/hooks/useUnifiedContacts";
import * as tracking from "lib/tracking";

// DevCycleProvider closes and recreates its client whenever `user` or `options` change identity,
// which also restarts the admin bar's DevCycleDebugger and orphans its iframes, so both must stay stable.
const DEV_CYCLE_OPTIONS = { enableEdgeDB: true };

// Guards against a same-browser double-fire while the (up to 5-min) better-auth session cookie
// cache still reports the flag as pending. The server flag is the cross-device / returning-user guard.
const signupConversionGuardKey = (userGuid: string) => `signup_conversion_fired:${userGuid}`;

/**
 * Builds the DevCycle user from the fetched current user. Keep customData in sync with
 * apps/webapp/config/initializers/dvc_features.rb, pages/admin/AdminComponent.tsx, and
 * nodejs/lib/shared/featureFlags/featureFlags.service.ts. Returns undefined for guests (no user),
 * matching the previous behaviour where DevCycle initialised without a user.
 * @param user The current user data fetched from the API.
 * @returns A DevCycleUser object or undefined for guests.
 */
function buildDevCycleUser(user: CurrentUserData | undefined): DevCycleUser | undefined {
  if (!user) {
    return undefined;
  }
  return {
    user_id: user.guid,
    customData: {
      company_id: user.company.guid,
      company_name: user.company.name,
      team_id: user.team.id,
      team_name: user.team.name,
      has_frameworks: user.activeDataTypes.includes("FRAMEWORKS"),
      has_awards: user.activeDataTypes.includes("AWARDS"),
      has_contacts: user.activeDataTypes.includes("CONTACTS"),
      has_documents: user.activeDataTypes.includes("DOCUMENTS"),
      has_buyer_supplier_data: user.activeDataTypes.includes("BUYERS|SUPPLIERS"),
      subscription: user.subscription,
      partner_programme: user.company.partnerProgramme?.name || null,
      payment_type: user.company.paymentType ?? null,
    },
  };
}

/**
 * Fetches the current user (seeding the React Query cache that the rest of the app and the router
 * guards read from), then configures DevCycle, Sentry, and Amplitude from it. Uses React Query
 * suspense, so the surrounding <Suspense> renders the loading fallback and the surrounding
 * <ErrorBoundary> handles failures — exactly like useAppToken. Guests (no app token) have the query
 * disabled, so it doesn't suspend and renders with an undefined user. Must be rendered inside
 * QueryClientProvider + AppTokenProvider.
 */
export function CurrentUserConfiguredProviders({
  trackingData,
  children,
}: {
  trackingData?: tracking.EventData;
  children: ReactNode;
}): JSX.Element {
  const { data: currentUser } = useCurrentUser({ suspense: true, useErrorBoundary: true });
  const { data: session } = useQuery(betterAuthSessionQueryOptions());
  const { mutate: acknowledgeSignupConversion } = useAcknowledgeSignupConversion();
  const { logEvent } = tracking.useTracking();
  const signupConversionFired = useRef(false);
  const devCycleUser = useMemo(() => buildDevCycleUser(currentUser), [currentUser]);

  useEffect(() => {
    if (currentUser) {
      setUser({
        email: currentUser.email,
        company_id: currentUser.company.id,
        company_name: currentUser.company.name,
      });
      tracking.init(currentUser);
    }
  }, [currentUser]);

  // Fires the one-time signup conversion pixels + "account created" event once a newly created
  // better-auth user is authenticated. Keys off the session's `signupConversionPending` flag (set
  // by the auth-service create hook for every signup method), so it covers email/password
  // (post-verification login), social SSO, and enterprise SSO alike.
  useEffect(() => {
    const conversionUser = session?.user;

    if (
      !currentUser ||
      !conversionUser ||
      !session?.signupConversionPending ||
      signupConversionFired.current
    ) {
      return;
    }

    const pendingUserConversion = localStorage.getItem(signupConversionGuardKey(conversionUser.id));
    if (pendingUserConversion) return;

    // Set both guards before firing so a re-render can't double-fire.
    signupConversionFired.current = true;
    localStorage.setItem(signupConversionGuardKey(conversionUser.id), "1");

    logEvent(tracking.EventNames.userAccountCreated, {
      Email: conversionUser.email,
      Name: conversionUser.name,
    });
    void tracking.logUserConversion();
    acknowledgeSignupConversion();
  }, [session, currentUser, logEvent, acknowledgeSignupConversion]);

  return (
    <DevCycleProvider
      config={{
        sdkKey: window.dev_cycle_sdk_key,
        user: devCycleUser,
        options: DEV_CYCLE_OPTIONS,
      }}
    >
      <tracking.TrackingProviderWithFeatureFlags trackingData={trackingData ?? {}}>
        <UnifiedContactsUserProperty />
        {children}
      </tracking.TrackingProviderWithFeatureFlags>
    </DevCycleProvider>
  );
}

/**
 * Records whether the user has the unified contacts experience as an Amplitude user property, so
 * adoption can be split by it. Needs DevCycle, as the experience can come from a flag.
 */
function UnifiedContactsUserProperty(): null {
  const unifiedContactsEnabled = useUnifiedContacts();
  useEffect(() => {
    tracking.setUserProperties({ unified_contacts_enabled: unifiedContactsEnabled });
  }, [unifiedContactsEnabled]);
  return null;
}
